import type { FormAccessMode, FormDetail } from '../api/types';

export interface FormSettings {
  title: string;
  accessMode: FormAccessMode;
  primaryColour: string;
  containsSensitiveInfo: boolean;
  allowSaveAndResume: boolean;
}

interface Props {
  settings: FormSettings;
  disabled: boolean;
  onChange: (settings: FormSettings) => void;
}

const ACCESS_MODES: FormAccessMode[] = ['Public', 'Private', 'SecretLink'];

/** Reads the editable settings out of a loaded form. */
export function settingsFromForm(form: FormDetail): FormSettings {
  const settings = (form.settings ?? {}) as Record<string, unknown>;
  const theme = (form.theme ?? {}) as Record<string, unknown>;
  return {
    title: form.title,
    accessMode: form.accessMode,
    primaryColour: typeof theme.primaryColour === 'string' ? theme.primaryColour : '#2563eb',
    containsSensitiveInfo: settings.containsSensitiveInfo === true,
    allowSaveAndResume: settings.allowSaveAndResume === true,
  };
}

/** Per-form settings: title, access mode, branding colour, sensitive-info flag. */
export function FormSettingsPanel({ settings, disabled, onChange }: Props) {
  return (
    <div className="settings-panel">
      <h3>Settings</h3>

      <label className="field">
        <span>Title</span>
        <input
          type="text"
          value={settings.title}
          disabled={disabled}
          onChange={(e) => onChange({ ...settings, title: e.target.value })}
        />
      </label>

      <label className="field">
        <span>Access mode</span>
        <select
          value={settings.accessMode}
          disabled={disabled}
          onChange={(e) => onChange({ ...settings, accessMode: e.target.value as FormAccessMode })}
        >
          {ACCESS_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Brand colour</span>
        <input
          type="color"
          value={settings.primaryColour}
          disabled={disabled}
          onChange={(e) => onChange({ ...settings, primaryColour: e.target.value })}
        />
      </label>

      <label className="field field-checkbox">
        <input
          type="checkbox"
          checked={settings.containsSensitiveInfo}
          disabled={disabled}
          onChange={(e) => onChange({ ...settings, containsSensitiveInfo: e.target.checked })}
        />
        <span>Contains sensitive information (APP)</span>
      </label>

      <label className="field field-checkbox">
        <input
          type="checkbox"
          checked={settings.allowSaveAndResume}
          disabled={disabled}
          onChange={(e) => onChange({ ...settings, allowSaveAndResume: e.target.checked })}
        />
        <span>Allow respondents to save and finish later</span>
      </label>
    </div>
  );
}
